import { tileAt, type MapDef } from '../world/map';
import type { Decor } from '../world/mapdef';
import { BUILDING_WALLS, FLOORS, ROADS, ROOFS, T } from '../world/tiles';
import { hash2, noise2 } from './noise';
import { shade } from './paint';
import { paint2 } from './structures2';
import { wallDecor4 } from './props4';

type Ctx = CanvasRenderingContext2D;

const rect = (c: Ctx, col: string, x: number, y: number, w: number, h: number) => {
  c.fillStyle = col;
  c.fillRect(x, y, w, h);
};

function vgrad(c: Ctx, x: number, y: number, w: number, h: number, top: string, bottom: string): void {
  const g = c.createLinearGradient(0, y, 0, y + h);
  g.addColorStop(0, top);
  g.addColorStop(1, bottom);
  c.fillStyle = g;
  c.fillRect(x, y, w, h);
}

interface Ctx2 {
  c: Ctx;
  map: MapDef;
  tx: number;
  ty: number;
  at: (dx: number, dy: number) => string;
}

/** Paint every structural tile (walls, roofs, facades, hedges, markings...) overlapping a chunk. */
export function paintStructures(c: Ctx, map: MapDef, ox: number, oy: number, size: number): void {
  const tx0 = Math.max(0, Math.floor(ox / T) - 1), tx1 = Math.min(map.w - 1, Math.floor((ox + size) / T) + 1);
  const ty0 = Math.max(0, Math.floor(oy / T) - 1), ty1 = Math.min(map.h - 1, Math.floor((oy + size) / T) + 1);
  for (let ty = ty0; ty <= ty1; ty++) {
    for (let tx = tx0; tx <= tx1; tx++) {
      const t: Ctx2 = { c, map, tx, ty, at: (dx, dy) => tileAt(map, tx + dx, ty + dy) };
      paint(t, tileAt(map, tx, ty), tx * T, ty * T);
    }
  }
  for (const d of map.decor) if (d.wall) wallDecor(c, d);
}

function paint(t: Ctx2, ch: string, x: number, y: number): void {
  switch (ch) {
    case '#':
      return wall(t, x, y);
    case 'd':
      return doorway(t, x, y);
    case 'D':
      return t.map.indoor ? exitMat(t, x, y) : door(t, x, y);
    case 'h':
      return hedge(t, x, y);
    case 'f':
      return fence(t, x, y);
    case 't':
      return trackLines(t, x, y);
    case 'l':
      return laneLine(t, x, y);
    case '=':
      return crosswalk(t, x, y);
    case 's':
      return curb(t, x, y);
    case 'S':
      return sandbox(t, x, y);
    case 'c':
      return courtLines(t, x, y);
    case 'P':
      return poolEdge(t, x, y);
    case 'R':
    case 'U':
    case 'Y':
      return roof(t, ch, x, y);
    case 'B':
    case 'K':
    case 'w':
    case 'v':
    case 'A':
      return facade(t, ch, x, y);
    default:
      return paint2(t.c, t.at, t.tx, t.ty, ch, x, y);
  }
}

// --- Interiors -------------------------------------------------------------------

function wall(t: Ctx2, x: number, y: number): void {
  const { c, map } = t;
  const th = map.theme;
  const below = t.at(0, 1);
  if (!(FLOORS.has(below) || below === 'D')) {
    // Wall seen from above: dark cap with a soft bevel where it meets a floor.
    vgrad(c, x, y, T, T, shade(th.wallTop, 0.08), shade(th.wallTop, -0.08));
    const rim = shade(th.wallTop, 0.3);
    if (FLOORS.has(t.at(-1, 0))) rect(c, rim, x, y, 1.5, T);
    if (FLOORS.has(t.at(1, 0))) rect(c, rim, x + T - 1.5, y, 1.5, T);
    if (FLOORS.has(t.at(0, -1))) rect(c, rim, x, y, T, 1.5);
    return;
  }
  // Wall face: cap, crown moulding, wallpaper, wainscot panels, baseboard.
  rect(c, th.wallTop, x, y, T, 2.5);
  rect(c, shade(th.wallTop, 0.35), x, y + 2, T, 0.5);
  vgrad(c, x, y + 2.5, T, 8, th.wallLight, th.wallFace);
  rect(c, shade(th.wallFace, 0.12), x, y + 2.5, T, 1);
  rect(c, shade(th.wallFace, -0.12), x, y + 3.5, T, 0.5);
  if (map.id === 'home') {
    for (let i = 0; i < T; i += 4) for (let j = 5; j < 10; j += 3) rect(c, 'rgba(190,120,90,0.35)', x + i + ((j / 3) % 2) * 2, y + j, 1, 1);
  } else if (map.id === 'school') {
    for (let i = 0; i < T; i += 4) rect(c, 'rgba(255,255,255,0.35)', x + i + 1, y + 4, 0.5, 6);
  } else {
    for (let i = 0; i < T; i += 4) rect(c, 'rgba(0,0,0,0.06)', x + i, y + 4, 0.5, 6);
    rect(c, 'rgba(0,0,0,0.06)', x, y + 7, T, 0.5);
  }
  vgrad(c, x, y + 10, T, 4.5, shade(th.wainscot, 0.12), shade(th.wainscot, -0.1));
  rect(c, shade(th.wainscot, 0.35), x, y + 10, T, 0.75);
  // Raised panels.
  for (let i = 1; i < T; i += 8) {
    rect(c, shade(th.wainscot, -0.2), x + i, y + 11.25, 6, 0.5);
    rect(c, shade(th.wainscot, -0.2), x + i, y + 11.25, 0.5, 2.5);
    rect(c, shade(th.wainscot, 0.25), x + i + 0.5, y + 13.5, 5.5, 0.5);
    rect(c, shade(th.wainscot, 0.25), x + i + 5.5, y + 11.75, 0.5, 2);
  }
  rect(c, shade(th.wainscot, -0.4), x, y + 14.5, T, 1.5);
  rect(c, shade(th.wainscot, -0.1), x, y + 14.5, T, 0.5);
}

function doorway(t: Ctx2, x: number, y: number): void {
  const { c } = t;
  vgrad(c, x, y + T - 3, T, 3, '#8a5530', '#5e381f');
  rect(c, '#a8703f', x, y + T - 3, T, 0.5);
  if (t.at(-1, 0) === '#') {
    rect(c, '#5e381f', x, y, 2, T);
    rect(c, '#8a5530', x + 1.5, y, 0.5, T);
  }
  if (t.at(1, 0) === '#') {
    rect(c, '#5e381f', x + T - 2, y, 2, T);
    rect(c, '#3e2414', x + T - 2, y, 0.5, T);
  }
}

function exitMat(t: Ctx2, x: number, y: number): void {
  const { c } = t;
  c.fillStyle = '#5f8448';
  c.beginPath();
  c.roundRect(x + 1, y + 2.5, T - 2, 11, 2);
  c.fill();
  rect(c, '#7a9e5c', x + 2, y + 3.5, T - 4, 0.75);
  for (let i = 3; i < T - 3; i += 2) rect(c, 'rgba(0,0,0,0.12)', x + i, y + 5, 0.5, 7);
  c.fillStyle = 'rgba(255,244,200,0.9)';
  c.beginPath();
  c.moveTo(x + 5, y + 6.5);
  c.lineTo(x + 8, y + 9.5);
  c.lineTo(x + 11, y + 6.5);
  c.lineTo(x + 11, y + 8.5);
  c.lineTo(x + 8, y + 11.5);
  c.lineTo(x + 5, y + 8.5);
  c.fill();
}

// --- Exterior structures -------------------------------------------------------------

const ROOF_COLORS: Record<string, [string, string, string]> = {
  R: ['#c4573e', '#8c3424', '#ea8a68'],
  U: ['#4d6c9e', '#2f4a76', '#7b9bcc'],
  Y: ['#5d9856', '#3a6e3c', '#8cc47e'],
};

function roof(t: Ctx2, ch: string, x: number, y: number): void {
  const { c, tx, ty } = t;
  const [base, dark, light] = ROOF_COLORS[ch];
  rect(c, dark, x, y, T, T);
  // Overlapping curved tiles, 4px rows, each row slightly offset.
  for (let j = 0; j < T; j += 4) {
    const row = ty * 4 + j / 4;
    const off = (row % 2) * 2.5;
    for (let i = -5; i < T; i += 5) {
      const ix = x + i + off;
      const v = hash2(tx * 7 + Math.floor((i + 5) / 5), row, 91);
      const col = v < 0.15 ? shade(base, -0.12) : v > 0.88 ? shade(base, 0.1) : base;
      const g = c.createLinearGradient(0, y + j, 0, y + j + 4.5);
      g.addColorStop(0, shade(col, 0.18));
      g.addColorStop(1, shade(col, -0.18));
      c.fillStyle = g;
      c.beginPath();
      c.moveTo(ix, y + j);
      c.lineTo(ix + 5, y + j);
      c.lineTo(ix + 5, y + j + 3);
      c.quadraticCurveTo(ix + 2.5, y + j + 5, ix, y + j + 3);
      c.closePath();
      c.fill();
      c.fillStyle = 'rgba(255,255,255,0.18)';
      c.fillRect(ix + 0.5, y + j + 0.25, 3.5, 0.5);
    }
  }
  c.save();
  c.beginPath();
  c.rect(x, y, T, T);
  c.clip();
  // Weathering and moss.
  for (let k = 0; k < 6; k++) {
    const mx = x + hash2(tx, ty * 6 + k, 92) * T, my = y + hash2(tx * 6 + k, ty, 93) * T;
    if (noise2(mx / 20, my / 20, 94) > 0.66) {
      c.fillStyle = 'rgba(110,140,70,0.45)';
      c.beginPath();
      c.ellipse(mx, my, 1.6, 1, 0, 0, Math.PI * 2);
      c.fill();
    }
  }
  c.restore();
  if (!ROOFS.has(t.at(0, -1))) {
    vgrad(c, x, y, T, 3.5, light, base);
    rect(c, dark, x, y + 3, T, 0.75);
    rect(c, 'rgba(255,255,255,0.35)', x, y + 0.5, T, 0.5);
  }
  if (!ROOFS.has(t.at(0, 1))) {
    rect(c, dark, x, y + T - 2.5, T, 2.5);
    rect(c, shade(base, 0.1), x, y + T - 2.5, T, 0.5);
  }
  if (!ROOFS.has(t.at(-1, 0))) {
    vgrad(c, x, y, 2.5, T, shade(dark, 0.1), dark);
    rect(c, light, x + 2, y, 0.5, T);
  }
  if (!ROOFS.has(t.at(1, 0))) {
    rect(c, dark, x + T - 2.5, y, 2.5, T);
    rect(c, 'rgba(0,0,0,0.2)', x + T - 3, y, 0.5, T);
  }
}

function facade(t: Ctx2, ch: string, x: number, y: number): void {
  const { c, tx, ty } = t;
  const brick = ch === 'K' || ch === 'v' || (ch === 'A' && (t.at(-1, 0) === 'K' || t.at(1, 0) === 'K'));
  if (brick) {
    rect(c, '#8f4a36', x, y, T, T);
    for (let j = 0; j < T; j += 3) {
      const row = ty * 6 + j / 3;
      for (let i = (row % 2) * 3 - 3; i < T; i += 6) {
        const v = hash2(tx * 4 + Math.floor((i + 6) / 6), row, 95);
        const col = v < 0.2 ? '#a24f36' : v > 0.85 ? '#c76b4c' : '#b65c40';
        rect(c, col, x + i + 0.5, y + j + 0.5, 5, 2.25);
        rect(c, 'rgba(255,220,190,0.18)', x + i + 0.5, y + j + 0.5, 5, 0.5);
      }
    }
  } else {
    vgrad(c, x, y, T, T, '#f1e6cf', '#e2d4b6');
    for (let k = 0; k < 5; k++) {
      const px = x + hash2(tx * 5 + k, ty, 96) * T, py = y + hash2(tx, ty * 5 + k, 97) * T;
      rect(c, 'rgba(160,130,90,0.18)', px, py, 1, 0.5);
    }
  }
  if (ROOFS.has(t.at(0, -1))) vgrad(c, x, y, T, 5, 'rgba(30,15,8,0.5)', 'rgba(30,15,8,0)');
  else if (BUILDING_WALLS.has(t.at(0, -1))) {
    // Cornice between floors.
    rect(c, 'rgba(0,0,0,0.14)', x, y, T, 1.25);
    rect(c, 'rgba(255,255,255,0.22)', x, y + 1.25, T, 0.5);
  }
  if (!BUILDING_WALLS.has(t.at(0, 1))) {
    vgrad(c, x, y + T - 3, T, 3, '#9d978b', '#7b766c');
    rect(c, '#bdb7ab', x, y + T - 3, T, 0.5);
  }
  // Street-level life on the walls: AC units, drainpipes and grime streaks.
  if ((ch === 'B' || ch === 'K') && hash2(tx, ty, 131) < 0.14) {
    const ax = x + 3 + hash2(tx, ty, 132) * 7, ay = y + 3 + hash2(tx, ty, 133) * 5;
    rect(c, 'rgba(0,0,0,0.25)', ax + 0.5, ay + 4.5, 7, 1);
    rect(c, '#cfc9bd', ax, ay, 7, 4.5);
    rect(c, '#9d978b', ax, ay + 3.5, 7, 1);
    for (let i = 1; i < 7; i += 1.5) rect(c, 'rgba(0,0,0,0.25)', ax + i, ay + 0.75, 0.5, 2.5);
    rect(c, 'rgba(0,0,0,0.2)', ax + 1, ay + 1, 5, 0.5);
  }
  if (!BUILDING_WALLS.has(t.at(-1, 0)) && !ROOFS.has(t.at(-1, 0)) && hash2(tx, Math.floor(ty / 3), 134) < 0.45) {
    rect(c, 'rgba(0,0,0,0.18)', x + 1.5, y, 2, T);
    rect(c, brick ? '#6e3a2a' : '#b8b0a2', x + 1.5, y, 1.25, T);
  }
  if ((ch === 'w' || ch === 'v') && !BUILDING_WALLS.has(t.at(0, 1)) === false && t.at(0, 1) !== ch) {
    for (let i = 2; i < T - 2; i += 3) if (hash2(tx * 7 + i, ty, 135) < 0.4) vgrad(c, x + i, y + T - 4, 1, 4, 'rgba(0,0,0,0.16)', 'rgba(0,0,0,0)');
  }
  if (!BUILDING_WALLS.has(t.at(-1, 0)) && !ROOFS.has(t.at(-1, 0))) rect(c, 'rgba(0,0,0,0.22)', x, y, 1, T);
  if (!BUILDING_WALLS.has(t.at(1, 0)) && !ROOFS.has(t.at(1, 0))) rect(c, 'rgba(0,0,0,0.3)', x + T - 1, y, 1, T);
  if (ch === 'w' || ch === 'v') window_(t, x, y, ch);
  if (ch === 'A') awning(t, x, y);
}

function window_(t: Ctx2, x: number, y: number, ch: string): void {
  const { c, tx } = t;
  const up = t.at(0, -1) === ch, down = t.at(0, 1) === ch;
  const top = up ? y : y + 2.5, bottom = down ? y + T : y + T - 4;
  const left = t.at(-1, 0) === ch, right = t.at(1, 0) === ch;
  const x0 = x + (left ? 0 : 2), x1 = x + T - (right ? 0 : 2);
  // Frame.
  rect(c, '#f8f4ea', x0, top, x1 - x0, bottom - top);
  rect(c, 'rgba(0,0,0,0.18)', x0, bottom - 0.5, x1 - x0, 0.5);
  const gx0 = x0 + (left ? 0 : 1), gx1 = x1 - (right ? 0 : 1);
  const gy0 = top + (up ? 0 : 1), gy1 = bottom - (down ? 0 : 1);
  // Glass: sky reflection gradient + warm interior glimpse.
  const g = c.createLinearGradient(0, gy0 - (up ? T : 0), 0, gy1 + (down ? T : 0));
  g.addColorStop(0, '#b9e3f7');
  g.addColorStop(0.55, '#79b3dc');
  g.addColorStop(1, '#4a7fae');
  c.fillStyle = g;
  c.fillRect(gx0, gy0, gx1 - gx0, gy1 - gy0);
  // Curtains on the outer edges.
  const curtain = ['#f4c7c3', '#f7e3a6', '#c9e3c0', '#d6cdee'][Math.floor(hash2(Math.floor(tx / 2), t.ty, 98) * 4)];
  if (!left) {
    c.fillStyle = curtain;
    c.beginPath();
    c.moveTo(gx0, gy0);
    c.lineTo(gx0 + 3, gy0);
    c.quadraticCurveTo(gx0 + 1.5, (gy0 + gy1) / 2, gx0 + 2.5, gy1);
    c.lineTo(gx0, gy1);
    c.fill();
  }
  if (!right) {
    c.fillStyle = curtain;
    c.beginPath();
    c.moveTo(gx1, gy0);
    c.lineTo(gx1 - 3, gy0);
    c.quadraticCurveTo(gx1 - 1.5, (gy0 + gy1) / 2, gx1 - 2.5, gy1);
    c.lineTo(gx1, gy1);
    c.fill();
  }
  // Diagonal glare.
  c.save();
  c.beginPath();
  c.rect(gx0, gy0, gx1 - gx0, gy1 - gy0);
  c.clip();
  c.fillStyle = 'rgba(255,255,255,0.45)';
  c.beginPath();
  c.moveTo(gx0 + 4, gy0);
  c.lineTo(gx0 + 6, gy0);
  c.lineTo(gx0, gy0 + 6);
  c.lineTo(gx0, gy0 + 4);
  c.fill();
  c.restore();
  // Mullions.
  if (!right) rect(c, '#f8f4ea', x1 - (x1 - x0) / 2 - 0.5, top, 1, bottom - top);
  if (!down) {
    // Sill + flower box.
    vgrad(c, x0 - 1, bottom, x1 - x0 + 2, 1.5, '#ffffff', '#cfc8ba');
    vgrad(c, x0, bottom + 1.5, x1 - x0, 2.5, '#8a5530', '#5e381f');
    for (let i = 0; i < (x1 - x0) / 2.5; i++) {
      const fx = x0 + 1 + i * 2.5;
      rect(c, '#3f8a3a', fx, bottom, 1.5, 1.5);
      c.fillStyle = ['#ff6f91', '#ffd34e', '#ffffff', '#c58cff', '#ff9f43'][(tx + i) % 5];
      c.beginPath();
      c.arc(fx + 0.75, bottom - 0.25, 0.9, 0, Math.PI * 2);
      c.fill();
    }
  }
}

function awning(t: Ctx2, x: number, y: number): void {
  const { c, tx } = t;
  for (let i = 0; i < T; i += 4) {
    const green = (tx * 4 + i) % 8 === 0;
    vgrad(c, x + i, y, 4, 10, green ? '#3bbd7c' : '#ffffff', green ? '#24865a' : '#dedad0');
    c.fillStyle = green ? '#24865a' : '#dedad0';
    c.beginPath();
    c.arc(x + i + 2, y + 10, 2, 0, Math.PI);
    c.fill();
  }
  vgrad(c, x, y + 11, T, 4, 'rgba(0,0,0,0.3)', 'rgba(0,0,0,0)');
}

function door(t: Ctx2, x: number, y: number): void {
  const { c } = t;
  const left = t.at(-1, 0) === 'D', right = t.at(1, 0) === 'D';
  const x0 = x + (left ? 0 : 2), x1 = x + T - (right ? 0 : 2);
  vgrad(c, x0 - (left ? 0 : 1), y - 3, x1 - x0 + (left ? 0 : 1) + (right ? 0 : 1), T + 3, '#5e381f', '#3a2214');
  vgrad(c, x0 + 0.5, y - 1.5, x1 - x0 - 1, T + 0.5, '#a36a40', '#7a4a2a');
  // Panels and glass.
  rect(c, '#c4ecff', x0 + 2, y + 0.5, x1 - x0 - 4, 4.5);
  rect(c, 'rgba(255,255,255,0.7)', x0 + 2.5, y + 1, 1, 3.5);
  rect(c, 'rgba(0,0,0,0.2)', x0 + 2, y + 7, x1 - x0 - 4, 0.5);
  rect(c, 'rgba(0,0,0,0.2)', x0 + 2, y + 7, 0.5, 5);
  rect(c, 'rgba(255,255,255,0.18)', x0 + 2, y + 12, x1 - x0 - 4, 0.5);
  if (!right) {
    c.fillStyle = '#ffd34e';
    c.beginPath();
    c.arc(x1 - 2.5, y + 8.5, 0.9, 0, Math.PI * 2);
    c.fill();
  }
  vgrad(c, x - 1, y + T - 1.5, T + 2, 1.5, '#cfc8ba', '#9a9488');
  if (!left) {
    // Porch lamp.
    rect(c, '#3a2214', x - 1.5, y - 3, 1.5, 4);
    rect(c, '#fff0b0', x - 1.25, y - 2.5, 1, 2);
  }
}

function hedge(t: Ctx2, x: number, y: number): void {
  const { c, tx, ty } = t;
  const front = t.at(0, 1) !== 'h';
  const topEdge = t.at(0, -1) !== 'h';
  vgrad(c, x, y, T, T, '#2f7a3a', '#245f2d');
  // Leaf clumps lit from the top-left.
  for (let k = 0; k < 14; k++) {
    const lx = x + hash2(tx * 14 + k, ty, 101) * T, ly = y + hash2(tx, ty * 14 + k, 102) * (front ? T - 4 : T);
    const r = 1.6 + hash2(tx + k, ty + k, 103) * 1.6;
    c.fillStyle = '#23592b';
    c.beginPath();
    c.arc(lx + 0.6, ly + 0.6, r, 0, Math.PI * 2);
    c.fill();
    c.fillStyle = ['#3f9a4a', '#4dab55', '#378e43'][k % 3];
    c.beginPath();
    c.arc(lx, ly, r, 0, Math.PI * 2);
    c.fill();
    c.fillStyle = 'rgba(190,240,150,0.5)';
    c.beginPath();
    c.arc(lx - r * 0.35, ly - r * 0.35, r * 0.35, 0, Math.PI * 2);
    c.fill();
  }
  if (topEdge) {
    for (let i = 0; i < T; i += 2.5) {
      c.fillStyle = '#5fbd5f';
      c.beginPath();
      c.arc(x + i + 1, y + 1.2, 1.4, 0, Math.PI * 2);
      c.fill();
    }
  }
  if (front) {
    vgrad(c, x, y + T - 4, T, 4, '#1f5226', '#163d1c');
    vgrad(c, x, y + T, T, 3, 'rgba(20,40,15,0.45)', 'rgba(20,40,15,0)');
  }
}

function fence(t: Ctx2, x: number, y: number): void {
  const { c } = t;
  vgrad(c, x, y + 12, T, 3, 'rgba(20,30,10,0.3)', 'rgba(20,30,10,0)');
  vgrad(c, x, y + 5, T, 1.5, '#ffffff', '#d8d2c4');
  vgrad(c, x, y + 9.5, T, 1.5, '#ffffff', '#d8d2c4');
  for (let i = 1; i < T; i += 4) {
    rect(c, 'rgba(0,0,0,0.15)', x + i + 2.5, y + 2, 0.75, 11);
    vgrad(c, x + i, y + 1.5, 2.5, 11.5, '#ffffff', '#ddd6c8');
    c.fillStyle = '#ffffff';
    c.beginPath();
    c.moveTo(x + i, y + 1.5);
    c.lineTo(x + i + 1.25, y);
    c.lineTo(x + i + 2.5, y + 1.5);
    c.fill();
    rect(c, 'rgba(0,0,0,0.12)', x + i + 2, y + 1.5, 0.5, 11.5);
  }
}

function trackLines(t: Ctx2, x: number, y: number): void {
  const { c } = t;
  const line = 'rgba(255,255,255,0.85)';
  if (t.at(0, -1) !== 't') rect(c, line, x, y, T, 0.75);
  if (t.at(0, 1) !== 't') rect(c, line, x, y + T - 0.75, T, 0.75);
  if (t.at(-1, 0) !== 't') rect(c, line, x, y, 0.75, T);
  if (t.at(1, 0) !== 't') rect(c, line, x + T - 0.75, y, 0.75, T);
  const vert = t.at(0, -1) === 't' && t.at(0, 1) === 't' && (t.at(-1, 0) !== 't' || t.at(1, 0) !== 't');
  if (vert) rect(c, 'rgba(255,255,255,0.55)', t.at(-1, 0) !== 't' ? x + T - 0.5 : x, y, 0.5, T);
  else if (t.at(0, -1) !== 't' || t.at(0, 1) !== 't') rect(c, 'rgba(255,255,255,0.55)', x, t.at(0, -1) !== 't' ? y + T - 0.5 : y, T, 0.5);
}

function laneLine(t: Ctx2, x: number, y: number): void {
  if (t.tx % 2) return;
  const { c } = t;
  for (let i = 0; i < 12; i += 0.5) {
    const worn = noise2((x + i) * 0.4, y * 0.1, 111);
    c.fillStyle = `rgba(245,228,150,${0.55 + worn * 0.4})`;
    c.fillRect(x + 2 + i, y + T - 1, 0.5, 1.5);
  }
}

function crosswalk(t: Ctx2, x: number, y: number): void {
  const { c } = t;
  for (let j = 1; j < T; j += 6) {
    for (let i = 0; i < T; i += 1) {
      const worn = noise2((x + i) * 0.3, (y + j) * 0.3, 112);
      if (worn < 0.18) continue;
      c.fillStyle = `rgba(240,240,232,${0.65 + worn * 0.35})`;
      c.fillRect(x + i, y + j, 1, 3);
    }
  }
}

function curb(t: Ctx2, x: number, y: number): void {
  const { c } = t;
  if (ROADS.has(t.at(0, 1))) {
    vgrad(c, x, y + T - 3, T, 3, '#e4dfd4', '#8f897d');
    for (let i = 0; i < T; i += 8) rect(c, 'rgba(0,0,0,0.2)', x + i, y + T - 3, 0.5, 3);
  }
  if (ROADS.has(t.at(0, -1))) {
    rect(c, '#e4dfd4', x, y, T, 1);
    vgrad(c, x, y + 1, T, 2, 'rgba(0,0,0,0.15)', 'rgba(0,0,0,0)');
  }
}

function sandbox(t: Ctx2, x: number, y: number): void {
  const { c } = t;
  const plank = (px: number, py: number, w: number, h: number) => {
    vgrad(c, px, py, w, h, '#c08048', '#8a5530');
    rect(c, 'rgba(255,230,190,0.35)', px, py, w, 0.5);
  };
  if (t.at(0, -1) !== 'S') plank(x, y, T, 2);
  if (t.at(0, 1) !== 'S') plank(x, y + T - 2, T, 2);
  if (t.at(-1, 0) !== 'S') plank(x, y, 2, T);
  if (t.at(1, 0) !== 'S') plank(x + T - 2, y, 2, T);
}

function courtLines(t: Ctx2, x: number, y: number): void {
  const { c } = t;
  const line = 'rgba(255,255,255,0.9)';
  const inner = (dx: number, dy: number) => t.at(dx, dy) === 'c';
  // Outer boundary one tile in from the apron, plus the service lines.
  if (!inner(0, -2) && inner(0, -1)) rect(c, line, x, y, T, 1);
  if (!inner(0, 2) && inner(0, 1)) rect(c, line, x, y + T - 1, T, 1);
  if (!inner(-2, 0) && inner(-1, 0)) rect(c, line, x, y, 1, T);
  if (!inner(2, 0) && inner(1, 0)) rect(c, line, x + T - 1, y, 1, T);
  if (inner(0, -1) && inner(0, 1) && inner(-1, 0) && inner(1, 0) && t.tx % 5 === 2) rect(c, 'rgba(255,255,255,0.6)', x + T / 2, y, 0.75, T);
}

function poolEdge(t: Ctx2, x: number, y: number): void {
  const { c } = t;
  const edge = '#e8f2f6';
  if (t.at(0, -1) !== 'P') vgrad(c, x, y, T, 3, edge, '#a9c6d2');
  if (t.at(-1, 0) !== 'P') rect(c, edge, x, y, 2, T);
  if (t.at(1, 0) !== 'P') rect(c, edge, x + T - 2, y, 2, T);
  if (t.at(0, 1) !== 'P') rect(c, edge, x, y + T - 2, T, 2);
}

// --- Wall-mounted decor ---------------------------------------------------------------

function framed(c: Ctx, x: number, y: number, w: number, h: number, frame: string): void {
  rect(c, 'rgba(0,0,0,0.25)', x + 1, y + 1, w, h);
  vgrad(c, x, y, w, h, shade(frame, 0.15), shade(frame, -0.15));
}

function wallDecor(c: Ctx, d: Decor): void {
  const { x, y } = d;
  switch (d.kind) {
    case 'window': {
      const w = d.w ?? 32;
      framed(c, x + 1, y + 2, w - 2, 12, '#7a4a2a');
      const g = c.createLinearGradient(0, y + 3, 0, y + 13);
      g.addColorStop(0, '#9fd6ff');
      g.addColorStop(0.6, '#d8f0ff');
      g.addColorStop(1, '#fff1c8');
      c.fillStyle = g;
      c.fillRect(x + 2.5, y + 3.5, w - 5, 9);
      // Distant hills and a cloud.
      c.fillStyle = '#8cc77a';
      c.beginPath();
      c.moveTo(x + 2.5, y + 12.5);
      c.quadraticCurveTo(x + w * 0.3, y + 8, x + w * 0.55, y + 11);
      c.quadraticCurveTo(x + w * 0.8, y + 8.5, x + w - 2.5, y + 11);
      c.lineTo(x + w - 2.5, y + 12.5);
      c.fill();
      c.fillStyle = 'rgba(255,255,255,0.9)';
      c.beginPath();
      c.ellipse(x + w * 0.35, y + 6, 3, 1.2, 0, 0, Math.PI * 2);
      c.ellipse(x + w * 0.4, y + 5.3, 2, 1.2, 0, 0, Math.PI * 2);
      c.fill();
      for (let i = x + 16; i < x + w - 4; i += 16) rect(c, '#7a4a2a', i, y + 3.5, 1, 9);
      rect(c, 'rgba(255,255,255,0.6)', x + 3.5, y + 4, 1, 6);
      return;
    }
    case 'clock':
      c.fillStyle = '#3a2214';
      c.beginPath();
      c.arc(x + 6, y + 6, 6, 0, Math.PI * 2);
      c.fill();
      c.fillStyle = '#fff8e6';
      c.beginPath();
      c.arc(x + 6, y + 6, 5, 0, Math.PI * 2);
      c.fill();
      rect(c, '#3a2214', x + 5.75, y + 2.5, 0.5, 3.5);
      rect(c, '#3a2214', x + 6, y + 5.75, 2.5, 0.5);
      return;
    case 'poster':
      framed(c, x, y, 14, 10, d.variant ? '#e2493b' : '#d2a64a');
      rect(c, '#fff6dc', x + 1.5, y + 1.5, 11, 7);
      rect(c, d.variant ? '#3a8fd9' : '#e8735a', x + 3.5, y + 2.5, 7, 3);
      rect(c, '#555', x + 3.5, y + 6.5, 5, 0.5);
      return;
    case 'blackboard': {
      const w = d.w ?? 128;
      framed(c, x, y + 1, w, 13, '#7a4a2a');
      vgrad(c, x + 1.5, y + 2.5, w - 3, 10, '#35664f', '#2a5240');
      c.strokeStyle = 'rgba(255,255,255,0.75)';
      c.lineWidth = 0.6;
      c.beginPath();
      c.moveTo(x + 8, y + 6);
      c.bezierCurveTo(x + 14, y + 4, x + 20, y + 8, x + 30, y + 5.5);
      c.moveTo(x + 8, y + 9);
      c.lineTo(x + 48, y + 9);
      c.moveTo(x + 58, y + 5);
      c.lineTo(x + 90, y + 5);
      c.stroke();
      rect(c, '#f4f1ea', x + w - 20, y + 12.5, 6, 1.5);
      return;
    }
    case 'painting': {
      const cols = [['#3a8fd9', '#ffd34e', '#57c278'], ['#e2493b', '#57c278', '#ffd34e'], ['#9b5fc0', '#ff9f43', '#ffe6f0']][d.variant ?? 0];
      framed(c, x, y, 16, 12, '#c08048');
      vgrad(c, x + 1.5, y + 1.5, 13, 9, cols[0], shade(cols[0], -0.2));
      c.fillStyle = cols[2];
      c.beginPath();
      c.arc(x + 11, y + 4, 1.6, 0, Math.PI * 2);
      c.fill();
      c.fillStyle = cols[1];
      c.beginPath();
      c.moveTo(x + 1.5, y + 10.5);
      c.quadraticCurveTo(x + 6, y + 5, x + 14.5, y + 10.5);
      c.fill();
      return;
    }
    case 'mapBoard':
      framed(c, x, y, 24, 12, '#3a2214');
      rect(c, '#9fd6ff', x + 1.5, y + 1.5, 21, 9);
      rect(c, '#57c278', x + 3, y + 3, 8, 5);
      rect(c, '#e2b33b', x + 12, y + 5, 7, 3);
      return;
    case 'curtain':
      vgrad(c, x, y, 16, 14, '#ffd1dd', '#f2a6ba');
      for (let i = 2; i < 16; i += 3) rect(c, 'rgba(200,90,120,0.3)', x + i, y + 1, 0.75, 12);
      return;
    default:
      wallDecor4(c, d);
  }
}
